/**
 * DEV role simulator — synthesize an "effective me" from the current role
 * impersonation state, WITHOUT touching the real CEO account in the DB.
 *
 * The presets mirror `backend/rbac.py::PERMISSIONS`. Family wildcards
 * (`turni.*`, `orders.*`, …) are preserved intact — `hasCap` / `hasAnyCapUnder`
 * already understand them, so we don't need to expand them client-side.
 *
 * The picker itself remains visible ONLY for a real global CEO
 * (`useAuth().me.is_global` — the REAL me, not the impersonated one). This is
 * enforced in Layout.jsx.
 */
import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';

const DEV_TOOLS_ENABLED = process.env.REACT_APP_SHOW_DEV_TOOLS !== 'false';

// legacy role id (as in AppContext.ruoli) → { role_code, is_global, effective_capabilities }
const ROLE_PRESETS = {
  ceo: {
    role_codes: ['CEO'],
    is_global: true,
    effective_capabilities: ['*'],
  },
  titolare: {
    role_codes: ['TITOLARE'],
    is_global: false,
    effective_capabilities: [
      'company.view', 'company.update',
      'location.view', 'location.create', 'location.update', 'location.delete',
      'department.*',
      'employee.*', 'assignment.*',
      'user.view', 'user.create', 'user.update', 'user.delete',
      'audit.view',
      'security.users.view', 'security.users.create', 'security.users.update',
      'security.users.suspend', 'security.users.archive', 'security.users.restore',
      'security.users.revoke_sessions',
      'security.roles.manage', 'security.scopes.manage', 'security.capabilities.manage',
      'personale.view', 'personale.edit', 'personale.create',
      'personale.assignments.manage', 'personale.contract.manage',
      'personale.documents.manage', 'personale.job_roles.manage',
      'personale.archive', 'personale.view_payroll',
      'ferie.view', 'ferie.request', 'ferie.approve', 'ferie.balance.manage', 'ferie.config.manage',
      'turni.view', 'turni.create', 'turni.edit', 'turni.publish', 'turni.replace',
      'turni.*', 'orders.*', 'warehouse.*', 'attendance.*',
    ],
  },
  resp_generale: {
    role_codes: ['RESP_GEN'],
    is_global: false,
    effective_capabilities: [
      'company.view', 'location.view', 'department.view',
      'employee.*', 'assignment.*',
      'personale.view', 'personale.edit',
      'personale.assignments.manage', 'personale.documents.manage',
      'ferie.view', 'ferie.approve',
      'turni.view', 'turni.create', 'turni.edit', 'turni.replace',
      'attendance.view', 'attendance.correct',
    ],
  },
  resp_locale: {
    role_codes: ['RESP_LOCALE'],
    is_global: false,
    effective_capabilities: [
      'company.view', 'location.view',
      'department.view', 'department.update',
      'employee.view', 'employee.create', 'employee.update',
      'assignment.view', 'assignment.create', 'assignment.update',
      'personale.view', 'personale.edit',
      'ferie.view', 'ferie.approve',
      'turni.view', 'turni.create', 'turni.edit', 'turni.replace',
      'attendance.view', 'attendance.correct',
    ],
  },
  segretaria: {
    role_codes: ['SEGRETERIA'],
    is_global: false,
    effective_capabilities: [
      'company.view', 'location.view', 'department.view',
      'employee.view', 'employee.update',
      'assignment.view',
      'personale.view', 'personale.documents.manage',
      'ferie.view', 'attendance.view',
    ],
  },
  dipendente: {
    role_codes: ['DIPENDENTE'],
    is_global: false,
    effective_capabilities: [
      'self.view', 'ferie.request', 'ferie.view',
      'attendance.self', 'turni.view',
      'availability.self.manage',
    ],
  },
};

/**
 * Effective identity used by capability-driven UI (Layout, Dashboard, guards).
 * When impersonation is OFF → returns the real `me` unchanged.
 * When ON → returns a synthetic `me` with role_codes / is_global / effective_capabilities
 * overridden by the selected preset. `id / email / full_name` stay from the real
 * account so audit and downstream views don't break, but every capability decision
 * follows the impersonated role.
 */
export function useEffectiveMe() {
  const { me } = useAuth();
  const { devImpersonateRoleId } = useApp();
  return useMemo(() => {
    if (!me) return me;
    if (!DEV_TOOLS_ENABLED || !devImpersonateRoleId || devImpersonateRoleId === 'ceo') return me;
    const preset = ROLE_PRESETS[devImpersonateRoleId];
    if (!preset) return me;
    return {
      ...me,
      role_codes: preset.role_codes,
      is_global: preset.is_global,
      effective_capabilities: preset.effective_capabilities,
      _impersonated: true,
      _impersonated_from: me.role_codes,
    };
  }, [me, devImpersonateRoleId]);
}
